import { Autocomplete, Grid, TextField } from "@mui/material";
import { GenericFormProps } from "../../Utils/Types";
import { Vocabulary } from "../../Utils/Vocabulary";
import { ClientModel, SellBookSchema } from "../../Utils/Models";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import {
  getValueForAutocomplete,
  isMobile,
  onFormError,
} from "../../Utils/Utils";
import { FormsIds, PaymentMethods } from "../../Utils/Constants";
import { postData } from "../../Services/postData";
import { endpoints } from "../../Utils/UrlEnum";
import withRole from "../../Hooks/withRole";

interface SellBookFormProps extends GenericFormProps {
  books: any[];
  clients: ClientModel[];
  restrictWorkPoints: boolean | undefined;
  clientId?: string;
  paymentMethods?: string[];
  onSearchClients?: (search: string | null) => void;
}

function SellBookForm(props: SellBookFormProps) {
  const { books, clients } = props;
  const { control, handleSubmit } = useForm({
    defaultValues: {
      clientId: props?.clientId,
    },
    resolver: yupResolver(SellBookSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });
  const collectPaymentType =
    props?.paymentMethods || Object.values(PaymentMethods);

  const onSubmit = (data: any) => {
    postData(`${endpoints.bookSales}`, data).then((res: any) => {
      if (res?.data) {
        props.onClose();
      }
    });
  };

  return (
    <form
      style={{ width: isMobile() ? "100%" : "500px" }}
      id={FormsIds.sellBookForm}
      onSubmit={handleSubmit(onSubmit, onFormError)}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <Controller
            name="bookId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                options={books || []}
                fullWidth
                getOptionLabel={(option) => option?.name}
                value={getValueForAutocomplete(books, field.value)}
                onChange={(_, newValue) => field.onChange(newValue?._id || "")}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={Vocabulary.book}
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <Controller
            name="quantity"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={Vocabulary.quantity}
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <Controller
            name="clientId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                fullWidth={true}
                freeSolo={false}
                options={clients || []}
                noOptionsText={Vocabulary.noResultsFound}
                getOptionLabel={(option: any) =>
                  option?.orderNumber +
                  " " +
                  option?.clientName +
                  " -  " +
                  option?.productName
                }
                value={getValueForAutocomplete(clients, field.value, "clientId")}
                onChange={(_, newValue) =>
                  field.onChange(newValue?.clientId || "")
                }
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id || option.clientId === value
                }
                disabled={!!props?.clientId}
                onInputChange={(e, value, reason) => {
                  if (reason === "input" && props.onSearchClients) {
                    if (value.length > 0) {
                      props.onSearchClients(value);
                    } else {
                      props.onSearchClients(null);
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={Vocabulary.cursant}
                    InputProps={{
                      ...params.InputProps,
                      rows: 4,
                      multiline: false,
                    }}
                    name="clientId"
                    variant="outlined"
                    size="medium"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          <Controller
            name={`paymentMethod`}
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Autocomplete
                value={field.value || undefined}
                freeSolo={false}
                disablePortal
                disableClearable
                clearOnBlur={true}
                options={collectPaymentType}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option === value;
                }}
                getOptionLabel={(option: any) =>
                  option?.management || option || ""
                }
                onChange={(event, value: any) => {
                  field.onChange(value);
                }}
                renderInput={(params) => (
                  <TextField
                    value={field?.value || " "}
                    error={!!error}
                    helperText={error?.message}
                    label={Vocabulary.paymentMethod}
                    {...params}
                    variant="outlined"
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>
    </form>
  );
}

export default withRole(SellBookForm);
